import React, { useState } from 'react';
import OptimizedImage from '../components/OptimizedImage';
import YouTubePlaylist from '../components/YouTubePlaylist';
import { imageConfig, altTexts } from '../utils/imageConfig';

const YouTube = () => {
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create email body for newsletter subscription
      const emailBody = `
Newsletter Subscription Request

Email: ${email}
Source: YouTube Page Newsletter Signup
Subscription Date: ${new Date().toLocaleString()}
Interest: YouTube content updates and launch notifications

This person has requested to be added to the Saner Consulting newsletter mailing list for YouTube channel updates.
      `;

      // Send email using the same SMTP configuration as contact form
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'Newsletter Subscriber',
          email: email,
          message: 'Newsletter subscription request from YouTube page',
          service: 'Newsletter'
        }),
      });

      if (response.ok) {
        alert('Thank you for subscribing! You will receive updates about our latest videos.');
        setEmail('');
      } else {
        throw new Error('Failed to send subscription request');
      }
    } catch (error) {
      console.error('Error sending newsletter subscription:', error);
      alert('There was an error processing your subscription. Please try again or contact us directly at contact@sanerconsulting.co.uk');
    }
  };

  return (
    <div className="youtube">
      {/* Hero Section */}
      <section className="section">
        <div className="container">
          <h1 className="hero-title">YouTube</h1>
          <p className="subtitle">
            @Saner-Youtube - Launching Soon!
          </p>
        </div>
      </section>

      {/* Channel Overview */}
      <section className="section section-dark">
        <div className="container">
          <div className="grid grid-2">
            <div>
              <h2 className="section-title">Saner Consulting</h2>
              <p className="card-description">
                We're preparing to launch our YouTube channel featuring cutting-edge content on business transformation, 
                AI implementation strategies, and expert insights. Stay tuned for valuable content designed to accelerate 
                your transformation journey.
              </p>
              <div className="channel-stats">
                <div className="stat-item">
                  <span className="stat-number">0</span>
                  <span className="stat-label">Subscribers</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">0</span>
                  <span className="stat-label">Videos</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">0</span>
                  <span className="stat-label">Total Views</span>
                </div>
              </div>
              <a href="https://youtube.com/@Saner-Youtube?sub_confirmation=1" className="btn btn-secondary external-link" target="_blank" rel="noopener noreferrer">
                Subscribe for Launch Updates
              </a>
            </div>
            <div className="youtube-hero-image">
              <OptimizedImage
                src={imageConfig.content.youtubeModernising}
                alt={altTexts.content.youtubeModernising}
                width="100%"
                height="auto"
                className="youtube-main-image"
                style={{ maxHeight: '400px', objectFit: 'cover', aspectRatio: '16/9' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content Categories */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">What You Can Expect</h2>
          <div className="grid grid-4">
            <div className="card">
              <OptimizedImage
                src={imageConfig.services.digitalStrategy}
                alt="Strategy Sessions and Business Planning"
                width="100%"
                height="150px"
                className="content-category-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Strategy Sessions</h3>
              <p className="card-description">
                Deep dives into digital transformation strategies, best practices, and lessons learned from successful implementations.
              </p>
            </div>
            <div className="card">
              <OptimizedImage
                src={imageConfig.technology.artificialIntelligence}
                alt="AI Insights and Machine Learning"
                width="100%"
                height="150px"
                className="content-category-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">AI Insights</h3>
              <p className="card-description">
                Latest developments in artificial intelligence, machine learning applications, and practical AI implementation guides.
              </p>
            </div>
            <div className="card">
              <OptimizedImage
                src={imageConfig.services.analyticsDashboard}
                alt="Case Studies and Business Analysis"
                width="100%"
                height="150px"
                className="content-category-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Case Studies</h3>
              <p className="card-description">
                Real-world transformation stories, client successes, and detailed analysis of business impact and ROI.
              </p>
            </div>
            <div className="card">
              <OptimizedImage
                src={imageConfig.technology.dataVisualization}
                alt="Technology Tutorials and Implementation"
                width="100%"
                height="150px"
                className="content-category-image"
                style={{ objectFit: 'cover', aspectRatio: '4/3' }}
              />
              <h3 className="card-title">Tech Tutorials</h3>
              <p className="card-description">
                Step-by-step guides on implementing new technologies, tools, and platforms for business transformation.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Upcoming Videos */}
      <section className="section section-dark">
        <div className="container">
          <h2 className="section-title">Upcoming Videos</h2>
          <div className="videos-grid">
            <div className="video-card">
              <OptimizedImage
                src={imageConfig.content.youtubeModernising}
                alt={altTexts.technology.dataVisualization}
                width="100%"
                height="auto"
                className="video-thumbnail-image"
                style={{ maxHeight: '200px', objectFit: 'cover', aspectRatio: '16/9' }}
              />
              <h3 className="video-title">Modernising Business Transformation with AI</h3>
              <p className="video-description">How artificial intelligence is revolutionizing traditional transformation approaches.</p>
              <span className="video-duration">Launching Soon</span>
            </div>
            <div className="video-card">
              <OptimizedImage
                src={imageConfig.content.youtubeAIAssistants}
                alt={altTexts.content.youtubeAIAssistants}
                width="100%"
                height="auto"
                className="video-thumbnail-image"
                style={{ maxHeight: '200px', objectFit: 'cover', aspectRatio: '16/9' }}
              />
              <h3 className="video-title">AI Assistants for Change Managers</h3>
              <p className="video-description">Leveraging AI tools to enhance change management effectiveness and efficiency.</p>
              <span className="video-duration">Launching Soon</span>
            </div>
            <div className="video-card">
              <OptimizedImage
                src={imageConfig.content.youtubeRAG}
                alt={altTexts.content.youtubeRAG}
                width="100%"
                height="auto"
                className="video-thumbnail-image"
                style={{ maxHeight: '200px', objectFit: 'cover', aspectRatio: '16/9' }}
              />
              <h3 className="video-title">RAG for Change Management</h3>
              <p className="video-description">Implementing Retrieval-Augmented Generation for smarter change initiatives.</p>
              <span className="video-duration">Launching Soon</span>
            </div>
            <div className="video-card">
              <OptimizedImage
                src={imageConfig.content.youtubeWillAI}
                alt={altTexts.content.youtubeWillAI}
                width="100%"
                height="auto"
                className="video-thumbnail-image"
                style={{ maxHeight: '200px', objectFit: 'cover', aspectRatio: '16/9' }}
              />
              <h3 className="video-title">Will AI Replace Change Experts?</h3>
              <p className="video-description">Exploring the future role of human expertise in an AI-driven transformation landscape.</p>
              <span className="video-duration">Launching Soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Recommended Videos from Others */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Latest Recommended Videos from Others</h2>
          <p className="section-description">
            Discover our curated selection of recommended videos from industry experts and thought leaders.
          </p>
          <YouTubePlaylist 
            playlistId="PLi-57khorsw3jBf6RcXtDytR7W2aWFik-" 
            maxResults={4} 
          />
          <div className="playlist-cta">
            <a 
              href="https://www.youtube.com/playlist?list=PLi-57khorsw3jBf6RcXtDytR7W2aWFik-" 
              className="btn btn-secondary external-link" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              View Full Playlist
            </a>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="section">
        <div className="container">
          <div className="cta-content">
            <h2 className="section-title">Stay Updated</h2>
            <p className="subtitle">
              Subscribe to our newsletter for updates on new videos, exclusive content, and upcoming live sessions.
            </p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input 
                type="email" 
                placeholder="Enter your email address" 
                className="newsletter-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <button type="submit" className="btn btn-primary">
                Subscribe to Newsletter
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default YouTube;